import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronRight,
  FaPhone,
  FaComment,
  FaUndo,
  FaPhoneSlash,
  FaCommentSlash,
} from "react-icons/fa";
import personaService from "../../../services/personaService";
import TopicCard from "../../topics/TopicCard";
import PersonaCard from "../../persona/PersonaCard";
import { useCardSelector } from "../../common/CardSelectorProvider";
import PromptWriter from "../../prompts/PromptWriter";
import yaml from "js-yaml";
import { useToast } from "../../common/ToastContext";
import DebuggerStateManager from "../../prompts/DebugStateManager";
import AdditionalAssignmentProperties from "../../prompts/AdditionalAssignmentProperties";
import topicService from "../../../services/topicService";
import { useNavigate } from "react-router-dom";
import { renderIcon, sessionIcon } from "../../common/IconLibrary";
import {
  preparePhoneInput,
  validateDialTarget,
} from "../../../utils/miscUtils";
import { handleTopicVisibilities } from "../../../utils/topicHelper";
import OnDemandConsoleModal from "./OnDemandConsoleModal";

const RecipientPhoneField = ({ value, onChange }) => (
  <input
    type="tel"
    inputMode="tel"
    maxLength={30}
    value={value}
    onChange={onChange}
    className="w-full mt-2 pl-8 p-2 bg-gray-700 text-white rounded"
    placeholder="Phone Number"
  />
);

const OnDemandConsole = () => {
  const location = useLocation();
  const passedTopic = location.state?.topic;
  const { openCardSelector } = useCardSelector();
  const [topics, setTopics] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [variables, setVariables] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sessionType, setSessionType] = useState("call");
  const [sessionStarted, setSessionStarted] = useState(false);
  const [usingCachedVariables, setUsingCachedVariables] = useState(false);
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [assignmentId, setAssignmentId] = useState("");
  const [additionalProperties, setAdditionalProperties] = useState(
    JSON.parse(localStorage.getItem("ondemand-additional-properties")) || {},
  );
  const [showAdditionalProperties, setShowAdditionalProperties] = useState(
    localStorage.getItem("ondemand-show-additional-properties") == "true" ||
      false,
  );
  const navigate = useNavigate();

  const extractValidationVariables = (topic) => {
    const storedVariables = localStorage.getItem(
      `ondemand-topic-${topic?.key}-variables`,
    );
    if (storedVariables) {
      setUsingCachedVariables(true);
      return storedVariables;
    }
    setUsingCachedVariables(false);
    const errorMessage =
      "# validation launch config\n# not found\n# provide variables as\n# key: value";
    const validationConfig = topic?.test_configs.find(
      (tc) => tc.name === "validation",
    );
    if (!validationConfig) {
      return errorMessage;
    }
    try {
      const variables = validationConfig.variables;
      let output = "";
      for (const key of Object.keys(variables)) {
        output += `${key}: ${variables[key]}\n`;
      }
      return output;
    } catch (error) {
      return errorMessage;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const topics = await topicService.getLatestTopicByVisibility("on-demand");
      const personas = await personaService.getAll();
      const sortedTopics = await handleTopicVisibilities(topics);
      setTopics(sortedTopics);
      setPersonas(personas);
      // select topic
      const prevTopic = topics.find(
        (t) => t.key === localStorage.getItem("ondemand-selected-topic") ?? "",
      );
      let selTopic = passedTopic ?? prevTopic ?? topics[0];
      // ensure we select the proper version of the topic (it may have changed)
      topics.forEach((t) => {
        if (t.key === selTopic.key && t.version !== selTopic.version) {
          selTopic = t;
          return;
        }
      });
      setSelectedTopic(selTopic);
      setTimeout(() => {
        // timeout to allow for monaco to load
        setVariables(extractValidationVariables(selTopic));
      }, 500);
      // select persona
      if (personas.length > 0) {
        const prevSelectedPersona = personas.find(
          (p) =>
            p.id === localStorage.getItem("ondemand-selected-persona") ?? "",
        );
        setSelectedPersona(prevSelectedPersona ?? personas[0]);
      }
      setName(localStorage.getItem("ondemand-recipient-name") ?? "");
      setPhoneNumber(
        localStorage.getItem("ondemand-recipient-phone-number") ?? "",
      );
    };
    fetchData();

    return () => {
      setVariables("");
    };
  }, [passedTopic]);

  const handleTopicSelect = () => {
    openCardSelector({
      items: topics,
      CardComponent: TopicCard,
      title: "Select a Topic",
      onSelect: (topic) => {
        setSelectedTopic(topic);
        setVariables(extractValidationVariables(topic));
        localStorage.setItem("ondemand-selected-topic", topic.key);
      },
      highlightVersions: ["On-Demand"],
      pinNamespace: "topics",
      getId: (t) => t.key,
    });
  };

  const handlePersonaSelect = () => {
    openCardSelector({
      items: personas,
      CardComponent: PersonaCard,
      title: "Select a Persona",
      onSelect: (persona) => {
        setSelectedPersona(persona);
        localStorage.setItem("ondemand-selected-persona", persona.id);
      },
      pinNamespace: "personas",
      getId: (p) => p.id,
    });
  };

  const clearCachedVariables = () => {
    if (selectedTopic) {
      localStorage.removeItem(`ondemand-topic-${selectedTopic.key}-variables`);
      setVariables(extractValidationVariables(selectedTopic));
      setUsingCachedVariables(false);
    }
  };

  const handlePropertyShow = () => {
    setShowAdditionalProperties(!showAdditionalProperties);
    localStorage.setItem(
      "ondemand-show-additional-properties",
      !showAdditionalProperties,
    );
  };

  const handlePropertyToggle = ({ key, prop }) => {
    let newProps;
    if (key in additionalProperties) {
      newProps = Object.fromEntries(
        Object.entries(additionalProperties).filter(
          ([propKey]) => propKey != key,
        ),
      );
    } else {
      newProps = { ...additionalProperties, [key]: prop };
    }

    setAdditionalProperties(newProps);
    localStorage.setItem(
      `ondemand-additional-properties`,
      JSON.stringify(newProps),
    );
  };

  const handlePropertyUpdate = (e) => {
    localStorage.setItem(`ondemand-additional-properties`, e);
    setAdditionalProperties(JSON.parse(e));
  };

  const buildLaunchConfig = (modality, phoneNbr, selectedTopic, props) => {
    let localVariables = {};
    try {
      localVariables = yaml.load(variables, { schema: yaml.JSON_SCHEMA }) || {}; //to treat all values as plain strings using the JSON_SCHEMA option, which disables date auto-parsing (bug 232679)
    } catch {
      // .. do nothing ..
    }
    Object.keys(localVariables).forEach((key) => {
      localVariables[key] = (localVariables[key] ?? "").toString();
    });
    const config = {
      persona_id: selectedPersona.id,
      modality: modality,
      variables: localVariables,
      recipient_phone_number: phoneNbr,
      topic: {
        key: selectedTopic.key,
        version: selectedTopic.version,
      },
    };
    for (const [key, value] of Object.entries(props)) {
      config[key] = value;
    }
    if (!config.variables) {
      config.variables = {};
    }
    const yaml_ = yaml.dump(config);
    return yaml_;
  };

  const pollForAssignmentId = () => {
    const poll = setInterval(() => {
      try {
        if (window.debuggerState?.assignmentId) {
          setAssignmentId(window.debuggerState.assignmentId);
          clearInterval(poll); // Stop polling once we have the assignmentId
        }
        if (!window.debuggerState?.isDebugging) {
          setAssignmentId(null);
          setSessionStarted(false);
          clearInterval(poll);
          if (window.debuggerState?.isOpenAssignment) {
            setShowModal(true);
          }
        }
      } catch (error) {
        console.error("Error polling for assignmentId:", error);
        clearInterval(poll); // Clear interval in case of an error
      }
    }, 500);
  };

  const updatePhoneNumber = (value) => {
    setPhoneNumber(value);
    localStorage.setItem("ondemand-recipient-phone-number", value);
  };

  const handlePhoneChange = (e) => {
    updatePhoneNumber(preparePhoneInput(e.target.value));
  };

  const handleLaunchSession = async (type) => {
    if (sessionStarted) {
      // stop the session, ensure the debugging is ended
      setSessionStarted(false);
      window.debuggerState.endDebugging();
      if (type === "call") {
        showToast(
          "The session has reset. Your phone call, if still in progress, will continue until you disconnect on your phone. If the session has been reset pre-maturely, you may see some wierd behavior on the Sessions page.",
          "info",
        );
      }
      return;
    }
    if (!selectedTopic || !selectedPersona) {
      showToast("Please fill in all fields (topic, persona)", "error");
      return;
    }

    const phoneNbr = validateDialTarget(phoneNumber, type);

    if (phoneNbr.error) {
      showToast(phoneNbr.error, "error");
      return;
    }
    if (!window.debuggerState) {
      showToast("Debugger is not initialized", "error");
      return;
    }
    if (window.debuggerState.isDebugging) {
      window.debuggerState.endDebugging();
      setSessionStarted(false);
      setSessionType(null);
      setAssignmentId(null);
    } else {
      const props = showAdditionalProperties ? additionalProperties : {};
      window.dispatchEvent(
        new CustomEvent("launchConfiguration", {
          detail: {
            name: "ondemand-testing",
            content: buildLaunchConfig(
              type === "call" ? "voice" : "text",
              phoneNbr.formatted,
              selectedTopic,
              props,
            ),
          },
        }),
      );
      setSessionStarted(true);
      setSessionType(type);
      pollForAssignmentId();
    }
  };

  const handleGoToSession = () => {
    navigate(`/sessions?assignment_id=${assignmentId}`);
  };

  return (
    <div className="absolute mt-4 top-1/2 w-full left-1/2 transform -translate-y-1/2 -translate-x-1/2">
      <DebuggerStateManager />
      <div className="flex text-gray-100 items-start justify-center dark-slim-scrollbar">
        <div className="flex-grow p-6 h-[95vh] max-w-[80vw]">
          <div className="flex space-y-6 flex-grow overflow-y-auto dark-slim-scrollbar mt-4">
            {/* Left Column - Persona and Recipient */}
            <div className="flex-1 flex-col gap-4 w-1/2 pr-3">
              {/* Caller section */}
              <div className="mt-[24px]">
                <button
                  onClick={handlePersonaSelect}
                  className="flex items-center justify-between w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                >
                  <span className="text-xl font-semibold">
                    {selectedPersona ? "Caller" : "Select a Caller"}
                  </span>
                  <FaChevronDown />
                </button>
                {selectedPersona && (
                  <div className="mt-1">
                    <PersonaCard
                      item={selectedPersona}
                      onClick={() => {}}
                      hideContextMenu={true}
                      height="500px"
                      compact={false}
                      hideFrontPageTopic={true}
                      hidePin={true}
                    />
                  </div>
                )}
              </div>

              {/* Recipient section */}
              <div>
                <div className="flex items-center justify-between text-white px-4 py-2 rounded-md">
                  <span className="text-xl font-semibold">
                    Recipient Phone Number
                  </span>
                </div>
                <div
                  className="card-glass mt-1 flex flex-col p-4"
                  style={{ height: "85px" }}
                >
                  <div className="flex items-start mb-4">
                    <img
                      src={"/default-avatar.png"}
                      alt={"Recipient Phone Number"}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div className="flex-grow">
                      <div className="relative">
                        <FaPhone className="absolute mt-2 left-2 top-3 text-gray-400" />
                        <RecipientPhoneField
                          value={phoneNumber}
                          onChange={handlePhoneChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Column - Topic and Variables */}
            <div className="flex-1 flex-col gap-4 w-1/2 pl-3">
              {/* Topic selection - now above variables */}
              <div>
                <button
                  onClick={handleTopicSelect}
                  className="flex items-center justify-between w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                >
                  <span className="text-xl font-semibold">
                    {selectedTopic ? "Topic" : "Select a Topic"}
                  </span>
                  <FaChevronDown />
                </button>
                {selectedTopic && (
                  <div className="mt-1">
                    <TopicCard
                      item={selectedTopic}
                      onClick={() => {}}
                      hideContextMenu={true}
                      height="300px"
                      highlightVersions={["On-Demand"]}
                      hidePin={true}
                    />
                  </div>
                )}
              </div>

              {/* Variables section */}
              <div>
                <div className="flex items-center justify-between text-white px-4 py-2 rounded-md">
                  <span className="text-xl font-semibold text-nowrap">
                    {usingCachedVariables ? "Custom " : "Default "}Variables
                  </span>
                  {usingCachedVariables && (
                    <div className="flex items-center">
                      <button
                        onClick={clearCachedVariables}
                        className="text-white px-2 py-1 rounded text-xs hover:bg-red-600 hover:bg-opacity-50 transition-colors"
                        title="Clear cached variables"
                      >
                        <FaUndo className="mr-1" />
                      </button>
                    </div>
                  )}
                </div>
                <div
                  className="card-glass mt-1 flex flex-col p-1"
                  style={{ height: "378px", background: "#1E1E1E" }}
                >
                  <PromptWriter
                    value={variables}
                    onChange={(e) => {
                      localStorage.setItem(
                        `ondemand-topic-${selectedTopic?.key}-variables`,
                        e,
                      );
                      setVariables(e);
                      setUsingCachedVariables(true);
                    }}
                    language="yaml"
                    readOnly={false}
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Additional Info */}
            <div className="flex-1 flex-col gap-4 w-1/2 pl-3">
              <button
                type="button"
                onClick={handlePropertyShow}
                className="flex items-center justify-between w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              >
                <span className="text-xl font-semibold">Additional Fields</span>
                {showAdditionalProperties ? (
                  <FaChevronDown />
                ) : (
                  <FaChevronRight />
                )}
              </button>

              {showAdditionalProperties && (
                <AdditionalAssignmentProperties
                  properties={additionalProperties}
                  onToggle={handlePropertyToggle}
                  onPropertyUpdate={handlePropertyUpdate}
                />
              )}
            </div>
          </div>

          {/* Action Buttons - kept at the bottom */}
          <div
            className="flex justify-center gap-4 pt-2 pb-4 clear-both"
            style={{ marginTop: "0px" }}
          >
            <button
              onClick={() => handleLaunchSession("call")}
              className={`flex items-center justify-center w-40 ${
                sessionStarted
                  ? sessionType === "call"
                    ? "bg-red-500 hover:bg-red-700"
                    : "hidden"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200`}
            >
              {sessionStarted && sessionType === "call" ? (
                <>
                  <FaPhoneSlash className="mr-2" /> Reset
                </>
              ) : (
                <>
                  <FaPhone className="mr-2" /> Start Call
                </>
              )}
            </button>
            {sessionStarted || <div className="w-10"></div>}
            <button
              onClick={() => handleLaunchSession("text")}
              className={`flex items-center justify-center w-40 ${
                sessionStarted
                  ? sessionType === "text"
                    ? "bg-red-500 hover:bg-red-700"
                    : "hidden"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200`}
            >
              {sessionStarted && sessionType === "text" ? (
                <>
                  <FaCommentSlash className="mr-2" /> Reset
                </>
              ) : (
                <>
                  <FaComment className="mr-2" /> Start Text
                </>
              )}
            </button>
            {/* Display assignmentId if available */}
            {sessionStarted && (
              <>
                <button
                  onClick={handleGoToSession}
                  className={`ml-10 flex items-center justify-center w-40 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 
                  rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 text-nowrap
                  ${assignmentId ? "" : "disabled"}`}
                >
                  {renderIcon(sessionIcon, 18, 18)} View Session
                </button>
              </>
            )}
          </div>
        </div>
        {showModal && (
          <OnDemandConsoleModal onClose={() => setShowModal(false)} />
        )}
      </div>
    </div>
  );
};

export default OnDemandConsole;
